import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useEffect } from 'react';
import _ from 'lodash';

import { useStorage } from '~Components/Storage';
import { useSelector } from '~Hooks';
import { useLogger } from '~Hooks/Entities/useLogger';
import { datadogLogs } from '@datadog/browser-logs';

import {
  GetVenueMetadata,
  GetVenueMetadataVariables,
  GetVenueMetadata_Venue_cashSources,
  GetVenueMetadata_Venue_receiptTemplate,
  UpdateMetadataMutation,
  UpdateMetadataMutationVariables,
} from './__queries__';

/**
 * Bag of metadata exposed to the rest of the app. Today this only holds
 * venue-level fields, but it can grow to include other metadata sources.
 *
 * When adding a field:
 *  1. Add it to this interface
 *  2. Add a default value to DEFAULT_METADATA
 *  3. Add it to the GET_VENUE_METADATA query (if venue-scoped)
 *  4. Map it in the effect that refreshes the cache
 */
/** A cash source (till/register) the venue can deposit to or withdraw from. */
export type CashSource = GetVenueMetadata_Venue_cashSources;

export interface Metadata {
  venueName: string;
  useCoursesInKDS: boolean;
  lateAfterMinutesInKDS: number;
  staffIdleTimeout: number | null;
  organizationId: string;
  legalName: string;
  zip: string;
  street: string;
  city: string;
  cashFloat: number; // How much cash to keep in the cash drawer between days
  cashSources: CashSource[];
  receiptTemplate: GetVenueMetadata_Venue_receiptTemplate;
  beeperList: string[];
  enableAutoCashEOD: boolean;
  hasIndividualEOD: boolean;
  discountExcludedCategoryIds: string[];
}

/**
 * Hardcoded fallbacks used until the GraphQL query resolves and writes the
 * real values into storage. Mirrors the DEFAULT_DATA pattern in Storage so
 * consumers never have to null-check metadata fields.
 */
export const DEFAULT_METADATA: Metadata = {
  venueName: '',
  useCoursesInKDS: false,
  lateAfterMinutesInKDS: 30,
  staffIdleTimeout: null,
  organizationId: '',
  legalName: '',
  zip: '',
  street: '',
  city: '',
  cashFloat: 0,
  cashSources: [],
  receiptTemplate: {
    __typename: 'ReceiptTemplate',
    id: '',
    name: '',
    introLines: [],
    bottomLines: [],
    image: {
      __typename: 'Image',
      id: '',
      file: { __typename: 'File', id: '', url: '' },
    },
  },
  beeperList: [],
  enableAutoCashEOD: false,
  hasIndividualEOD: false,
  discountExcludedCategoryIds: [],
};

/** Excluded from log payloads — not useful debugging info and needlessly bloats logs. */
const LOG_EXCLUDED_FIELDS = ['receiptTemplate'];

const fieldsForLog = (fields: string[]) =>
  fields.filter((field) => !LOG_EXCLUDED_FIELDS.includes(field));

const GET_VENUE_METADATA = gql`
  query GetVenueMetadata($venueId: ID) {
    Venue(id: $venueId) {
      id
      name
      useCoursesInKDS
      lateAfterMinutesInKDS
      staffIdleTimeout
      beeperList
      enableAutoCashEOD
      hasIndividualEOD
      discountExcludedCategoryIds

      organizationId
      legalName
      zip
      location {
        id
        name
        city
        street
        zip
      }

      receiptTemplate {
        id
        name
        introLines
        bottomLines
        image {
          id
          file {
            id
            url
          }
        }
      }

      cashFloat
      cashSources {
        id
        name
      }
    }
  }
`;

const UPDATE_VENUE_METADATA = gql`
  mutation UpdateMetadataMutation($input: UpdateVenueMetadataInput!) {
    updateVenueMetadata(input: $input) {
      success
      error
      message
    }
  }
`;

/**
 * Reads venue metadata, exposing the cached value from storage immediately
 * (so consumers don't have to wait on a query at boot) and refreshing it
 * in the background. Updates go through the metadata service and are
 * optimistically reflected in the cached value.
 */
export const useMetadata = () => {
  const venueId = useSelector((state) => state.venueId!);
  const [storage, commitToStorage] = useStorage();
  const logger = useLogger('use-metadata');

  const venueQuery = useQuery<GetVenueMetadata, GetVenueMetadataVariables>(GET_VENUE_METADATA, {
    variables: { venueId },
    skip: !venueId,
  });

  // Refresh the storage cache when a fresh value comes in from the server.
  useEffect(() => {
    const venue = venueQuery.data?.Venue;
    datadogLogs.setGlobalContextProperty('venue', venue?.name || DEFAULT_METADATA.venueName);

    if (!venue) {
      return;
    }

    const zipForVenue = venue?.zip || venue?.location?.zip?.toString();

    const fresh: Metadata = {
      venueName: venue.name || DEFAULT_METADATA.venueName,
      useCoursesInKDS: venue.useCoursesInKDS ?? DEFAULT_METADATA.useCoursesInKDS,
      lateAfterMinutesInKDS: venue.lateAfterMinutesInKDS ?? DEFAULT_METADATA.lateAfterMinutesInKDS,
      staffIdleTimeout: venue.staffIdleTimeout ?? DEFAULT_METADATA.staffIdleTimeout,
      organizationId: venue.organizationId || DEFAULT_METADATA.organizationId,
      legalName: venue.legalName || DEFAULT_METADATA.legalName,
      zip: zipForVenue || DEFAULT_METADATA.zip,
      street: venue.location.street || DEFAULT_METADATA.street,
      city: venue.location.city || DEFAULT_METADATA.city,
      receiptTemplate: venue.receiptTemplate || DEFAULT_METADATA.receiptTemplate,
      cashFloat: venue.cashFloat ?? DEFAULT_METADATA.cashFloat,
      cashSources: venue.cashSources ?? DEFAULT_METADATA.cashSources,
      beeperList: venue.beeperList ?? DEFAULT_METADATA.beeperList,
      enableAutoCashEOD: venue.enableAutoCashEOD ?? DEFAULT_METADATA.enableAutoCashEOD,
      hasIndividualEOD: venue.hasIndividualEOD ?? DEFAULT_METADATA.hasIndividualEOD,
      discountExcludedCategoryIds:
        venue.discountExcludedCategoryIds ?? DEFAULT_METADATA.discountExcludedCategoryIds,
    };

    if (!_.isEqual(fresh, storage.metadata)) {
      logger.info('Refreshing cached metadata from backend', {
        fields: fieldsForLog(Object.keys(fresh)),
      });
      commitToStorage({ metadata: fresh });
    }
  }, [venueQuery.data]);

  const [updateVenueMetadataMutation] = useMutation<
    UpdateMetadataMutation,
    UpdateMetadataMutationVariables
  >(UPDATE_VENUE_METADATA, {
    // The mutation doesn't return the updated Venue, so Apollo can't update
    // the cached metadata automatically.
    refetchQueries: ['GetVenueMetadata'],
    awaitRefetchQueries: true,
  });

  const update = async (input: Partial<Metadata>) => {
    if (!venueId) {
      logger.error('Cannot update metadata without a venueId', {
        fields: fieldsForLog(Object.keys(input)),
      });
      return;
    }

    // Optimistically update the cache so the UI reacts instantly.
    commitToStorage({ metadata: { ...storage.metadata, ...input } });

    try {
      const result = await updateVenueMetadataMutation({
        variables: { input: { venueId, ...input } },
      });

      if (!result.data?.updateVenueMetadata.success) {
        logger.error('Update venue metadata returned failure', {
          errorMessage: result.data?.updateVenueMetadata.error,
          fields: fieldsForLog(Object.keys(input)),
        });
      } else {
        logger.info('Updated venue metadata', { fields: fieldsForLog(Object.keys(input)) });
      }
    } catch (err) {
      logger.error('Failed to update venue metadata', {
        errorMessage: (err as Error).message,
        fields: fieldsForLog(Object.keys(input)),
      });
    }
  };

  return {
    data: storage.metadata,
    update,
    loading: venueQuery.loading,
    refetch: venueQuery.refetch,
  };
};
